import DOMPurify from 'dompurify'
import intl from 'react-intl-universal'
import { Action } from 'graphql/schemas/action/Action'
import { UserStudy } from 'graphql/schemas/user/UserStudy'
import { ALL_ACTIONS, NO_NEED_TO_BE_VIEWED_ACTIONS, NO_INTERACTION_ACTIONS } from 'constants/General'
import { showAlert } from 'components/utils/Alert'
import { isActionCompleted } from 'models/action_models'
import { isCourseExpired } from 'graphql/schemas/course/Course'
import { Company, subsDistributionParamsFactory } from 'graphql/schemas/company/Company'
import React from 'react'
declare var window

/**
 * Replaces new lines with an HTML <br> tag
 * @param str String to replace.
 */
export function nl2br(str) {
  return (`${str}`).replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br>$2')
}
export function nl2div(str) {
  return (`${str}`).replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '</div><div>$1')
}

/**
 * Replaces HTML <br> tags with new lines.
 * @param str HTML string to replace.
 */
export function br2nl(str) {
  return (`${str}`).replace(/<br\s*[\/]?>/gi, '\n')
}

export function contentTypeToUserAction(contentType: string) {
  switch (contentType) {
    case 'video': return 'watch'
    case 'audio': return 'listen'
    case 'text': return 'read'
    case 'quote': return 'quote'
    case 'link': return 'view'
    case 'external': return 'view'
    case 'image': return 'view'
    case 'qa': return 'do'
    case 'multiple_choice': return 'choose'
    case 'rating': return 'choose'
    case 'photo': return 'capture'
    case 'certificate': return 'graduate'
    case 'auth_web_link': return 'view'
    case 'assessment': return 'answer'
    // case 'discussion': return 'chat'
    // case 'note': return 'reflect'
    default: return ''
  }
}

export function getRelevantUserActions(contentType: string): string[] {
  let arr: string[] = []
  switch (contentType) {
    case 'video':
      return arr = ['watch']
    case 'audio':
      return arr = ['listen']
    case 'text':
      return arr = ['read', 'do']
    case 'quote':
      return arr = ['quote']
    case 'link':
    case 'external':
      return arr = ['view', 'watch', 'listen', 'read', 'do', 'dance']
    case 'auth_web_link':
    case 'image':
      return arr = ['view', 'listen', 'dance']
    case 'qa':
      return arr = ['do', 'reflect']
    case 'multiple_choice':
      return arr = ['do', 'choose', 'reflect', 'poll', 'quiz']
    case 'photo':
      return arr = ['do', 'capture', 'snap', 'selfie']
    case 'upload':
      return arr = ['attach', 'upload']
    case 'certificate':
      return arr = ['graduate']
    case 'assessment':
      return arr = ['answer', 'quiz']
    case 'word_puzzle':
      return arr = ['solve']
    default:
      return arr
  }
}

export function encode_utf8(s) {
  return unescape(encodeURIComponent(s))
}

export function decode_utf8(s) {
  return decodeURIComponent(escape(s))
}

export function isBeta(uid: string) {
  const testers = [
    '3EPBeE4NcevXuyRbx', // rkovac@gnowbe.com
    'H3ED4tDxQJcypt4az', // rkrulec@gnowbe.com
    '96kFXZrEAkfLrgBvQ', // rkrulec+pro@gnowbe.com
    'fnQqhPRmpTcM7yoSJ', // usmolnik@gnowbe.com
    'WvZoFdSve86CauoFp', // usmolnik+gnowbefy@gnowbe.com
  ]
  return testers.indexOf(uid) >= 0
}

/**
 * Copy a text to the clipboard. Uses HTML execCommand.
 * @param text Text to copy.
 */
export function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => { showAlert(intl.get('clipboard_copy')) })
      .catch((error) => { alert(`Copy failed! ${error}`) })
  }

  return false
}

export const throttle = (func, limit) => {
  let lastFunc
  let lastRan
  return function () {
    const context = this
    const args = arguments
    if (!lastRan) {
      func.apply(context, args)
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args)
          lastRan = Date.now()
        }
      }, limit - (Date.now() - lastRan))
    }
  }
}

export function debounce(func, wait: number, immediate?: boolean) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

/**
 * Shuffles a given array, and returns a new array with shuffled values.
 * @param array Array to shuffle.
 */
export function shuffle(array) {
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }
  return array
}

/**
 * Returns the real length of a HTML encoded string (useful for stuff like TrixEditor).
 * @param str String to calculate the length.
 */
export function realStringLength(str) {
  const el = document.createElement('div')
  el.innerHTML = str
  return el.innerHTML.length
}

/**
 * Returns the real length of a HTML encoded string (useful for stuff like TrixEditor).
 * @param str String to calculate the length.
 */
export function realWordCount(str) {
  const el = document.createElement('div')
  el.innerHTML = str
  return el.innerHTML.replace(/(<\/?[^>]+>)/g, ' ').split(/\s+/).filter(s => s !== '')
}

/**
 * Transforms links from normal text to clickable links.
 * @param inputText The text to linkify.
 * @param stopPropagation Wanna stop propagation when clicking the link?
 */
export function linkify(inputText: any, stopPropagation?: boolean) {
  if (typeof inputText.replace !== 'function') return

  let replacedText
  let replacePattern1
  let replacePattern2
  let replacePattern3

  // URLs starting with http://, https://, or ftp://
  // replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
  replacePattern1 = /((https?|ftps?):\/\/[^"<\s]+)(?![^<>]*>|[^"]*?<\/a)/gim
  replacedText = inputText.replace(replacePattern1, (p) => {
    const lastChar = p.substring(p.length - 1)
    if (lastChar.match(/[\.\-?!,]/)) {
      return `<a href="${p.substring(0, p.length - 1)}" target="_blank" ${(stopPropagation ? 'onclick="event.stopPropagation()"' : '')} rel="noopener noreferrer">${p.substring(0, p.length - 1)}</a>${lastChar}`
    }
    return `<a href="${p}" target="_blank" ${(stopPropagation ? 'onclick="event.stopPropagation()"' : '')} rel="noopener noreferrer">${p}</a>`
  })

  // URLs starting with "www." (without // before it, or it'd re-link the ones done above).
  // replacePattern2 = /(^|[^\/])(www\.[\w-.]+(\b|$))/gim
  // replacedText = replacedText.replace(replacePattern2, `$1<a href="https://$2" target="_blank" rel="noopener noreferrer" ${stopPropagation ? 'onclick="event.stopPropagation()"' : ''}>$2</a>`)

  // Change email addresses to mailto:: links.
  replacePattern3 = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gim
  replacedText = replacedText.replace(replacePattern3, (p) => {
    const lastChar = p.substring(p.length - 1)
    if (lastChar.match(/[\.\-?!,]/)) {
      return `<a href="<a href="mailto:${p.substring(0, p.length - 1)}" target="_blank" rel="noopener noreferrer" ${(stopPropagation ? 'onclick="event.stopPropagation()"' : '')}>${p.substring(0, p.length - 1)}</a>${lastChar}`
    }
    return `<a href="mailto:${p}" rel="noopener noreferrer" ${(stopPropagation ? 'onclick="event.stopPropagation()"' : '')}>${p}</a>`
  })

  return replacedText
}

export function isEditor(arr) {
  return arr.some((v) => {
    return ['admin', 'superEditor', 'editor'].indexOf(v) >= 0
  })
}

export function isSuperEditor(arr) {
  return arr.some((v) => {
    return ['admin', 'superEditor'].indexOf(v) >= 0
  })
}

/**
 * Returns a boolean if the value is found.
 * @param meta The array of meta values for the user
 * @param value The meta value you are looking for (/something/else)
 */
export function checkMeta(meta: any, value: string) {
  const split = value.split('/')
  let res = false
  res = !!meta
  if (res) {
    res = !!meta[split[1]]
  }
  else {
    return res
  }
  if (res && split[2]) {
    res = !!meta[split[1]][split[2]]
  }
  else {
    return res
  }
  if (res && split[2] && split[3]) {
    res = !!meta[split[1]][split[2]][split[3]]
  }
  else {
    return res
  }
  return res
}

export function getMeta(meta: any, key: string) {
  return meta?.general[key] || undefined
}

/**
 * Escapes the most common unsafe HTML elements.
 * @param unsafe The unsafe string to escape.
 */
export function escapeHtml(unsafe) {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export function unescapeHtml(unsafe) {
  return unsafe
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, '\'')
}

const _loadingScripts = {}
const _loadedScripts = {}
export function loadScript(url, type: 'js' | 'css') {

  if (_loadedScripts[url]) {
    return Promise.resolve()
  }

  if (_loadingScripts[url]) {
    let promiseFRes: Function
    const promise = new Promise<void>((fRes) => {
      promiseFRes = fRes
      _loadingScripts[url].push(promiseFRes)
    })
    return promise
  }

  _loadingScripts[url] = []

  const script = type === 'js'
    ? document.createElement('script')
    : document.createElement('link')
  if (type === 'js') {
    script.setAttribute('type', 'text/javascript')
  } else if (type === 'css') {
    script.setAttribute('rel', 'stylesheet')
    script.setAttribute('type', 'text/css')
  }

  return new Promise<void>((fRes) => {
    const onLoaded = () => {
      _loadedScripts[url] = true
      fRes()
      for (const promiseFRes of _loadingScripts[url] || []) {
        promiseFRes()
      }
      _loadingScripts[url] = null
    }
    if (script['readyState']) { // IE
      script['onreadystatechange'] = function () {
        if (script['readyState'] === 'loaded' || script['readyState'] === 'complete') {
          script['onreadystatechange'] = null
          onLoaded()
        }
      }
    } else { // Others
      script.onload = onLoaded
    }

    if (type === 'js') {
      script.setAttribute('src', url)
    } else if (type === 'css') {
      script.setAttribute('href', url)
    }

    document.getElementsByTagName('head')[0].appendChild(script)
  })
}

export function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export function trimStart(character, string) {
  let startIndex = 0
  while (string[startIndex] === character) {
    startIndex += 1
  }
  return string.substring(startIndex)
}

/**
 * Gets the width and height of the original image.
 * @param url The url to the original image.
 */
export function getImageSize(url) {
  const img = new Image()
  img.onload = () => {
    const height = img.height
    const width = img.width
    return { width, height }
  }
  img.src = url
}

/**
 * Parses the query string of the url and returns chunks.
 * @param url The search query to parse (?something=nice).
 * @param whole If passing the whole url (https://example.com/?something=nice).
 */
export function parse(url, whole = false) {
  let newUrl = url
  if (whole) {
    newUrl = url.substring(url.indexOf('?'))
  }
  let query: any = {}
  newUrl.substring(1).split('&').map((e) => {
    const el = e.split('=')
    query = {
      ...query,
      [el[0]]: el[1],
    }
  })
  return query as { [field: string]: any }
}

/**
 * Changes the given text to Camel Case.
 * @param text The text to camelCase.
 */
export function camelCase(text: string) {
  const arr: string[] = text.split(' ')
  const newArr = arr.reduce((vals, val) => {
    // tslint:disable-next-line: no-parameter-reassignment
    return vals = vals.concat([val.charAt(0).toLocaleUpperCase() + val.slice(1).toLocaleLowerCase()])
  }, [] as string[])
  return newArr.join(' ')
}

/**
 * Truncate a text and append ... if it is longer than length.
 * @param text The text to truncate.
 * @param length The length after which truncate. Optional, defaults to 100.
 */
export function truncate(text: string, length?: number) {
  const newLength = length || 100
  if (text.length > newLength) {
    return `${text.slice(0, newLength - 3)}...`
  }
  return text
}

/**
 * Calculates how many days went by since the beginning of time.
 * @param date The date for which we want to calculate the days.
 * @param timezone The timezone where you are located.
 */
export function getDaysFromEpoch(date: Date, timezone: number) {
  let millis = date.getTime()
  millis += timezone * 60 * 60 * 1000
  return Math.floor(millis / 3600 / 24 / 1000)
}

export function getDaysFromEpoch2(date: Date) {
  // sometimes must be ceil sometimes must be floor, probably based on DST
  // maybe round instead of ceil/floor?
  // back to floor, because of some weird edge cases with timezones behind unlocking date
  return Math.floor((date.getTime() - date.getTimezoneOffset() * 60000) / 8.64e7)
}

/**
 * Calculates the date from the number of days went by since the beginning of time.
 * @param date The days from the beginning of time.
 * @param timezone The timezone where you are located.
 */
export function getDateFromDaysFromEpoch(daysFromEpoch: number, timezone?: number) {
  const millis = daysFromEpoch * 3600 * 24 * 1000
  // millis -= timezone * 60*60*1000

  const date = new Date(millis)

  // If a timezone offset is provided, adjust the date
  // and we will provide timezone to avoid bug (issue) with selected date for unlocking
  // because if we are in different timezone, selected date (unlock date) will be automatically decrease by one
  // from 6/10/2023 to 5/10/2023
  if (timezone) {
    date.setMinutes(date.getMinutes() - (timezone * 60));
  }

  return date;
}

export function timer(callback, delay) {
  let id
  let started
  let remaining = delay
  let running

  this.start = function () {
    running = true
    started = new Date()
    id = setTimeout(callback, remaining)
  }

  this.pause = function () {
    running = false
    clearTimeout(id)
    remaining -= Number(new Date()) - started
  }

  this.getTimeLeft = function () {
    if (running) {
      this.pause()
      this.start()
    }
    return remaining
  }

  this.getStateRunning = function () {
    return running
  }

  this.reset = function () {
    running = false
    clearTimeout(id)
  }

  this.start()
}

export const createMarkup = (html: any, links?: boolean) => {
  DOMPurify.setConfig({ ADD_ATTR: ['target'] })

  if (links) {
    return { __html: linkify(DOMPurify.sanitize(html)) }
  }

  return { __html: DOMPurify.sanitize(html) }
}

export const sanitize = (html: any, allowTags?: string[]) => {
  if (!allowTags) return DOMPurify.sanitize(html)

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: allowTags })
}

export function isEmoji(str: string) {
  const emojis = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/
  const normal = /[\x20-\x7E]/
  if (emojis.test(str) && !normal.test(str)) {
    return true
  }
  return false
}

export function hash(str: string) {
  let hash = 0
  let i
  let chr
  if (str.length === 0) return hash
  for (i = 0; i < str.length; i += 1) {
    chr = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}


export function isElementInViewport(el) {
  if (!el) return false

  const rect = el.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && /*or $(window).height() */
    rect.right <= (window.innerWidth || document.documentElement.clientWidth) /*or $(window).width() */
  )
}

export function calculateEngagement(actions: Action[], studies: UserStudy[]): number {
  if (actions.every(a => NO_NEED_TO_BE_VIEWED_ACTIONS.includes(a.contentType))) return 100

  if (!studies) return 0

  let allActions = 0
  let doneActions = 0

  actions
    .forEach((action) => {
      if (ALL_ACTIONS.some(a => a === action.contentType) && !NO_NEED_TO_BE_VIEWED_ACTIONS.some(a => a === action.contentType)) {
        allActions += 1

        const study = studies.find(s => s.actionId === action.id && s.chapterId === action.chapterId) || null
        if (isActionCompleted(action.contentType, action.placeholderAnswer || '', study)) {
          doneActions += 1
        }
      }
    })

  return Math.round(allActions > 0 ? 100 * doneActions / allActions : 0)
}

/**
 * Takes a random entry out of an array.
 * @param arr Array of any kind
 */
export function randomSample(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

/**
 * Returns the md5 hash of a string.
 * @param d A string
 */
// tslint:disable-next-line
export const MD5 = function (d) { let result = M(V(Y(X(d), 8 * d.length))); return result.toLowerCase() }; function M(d) { for (var _, m = "0123456789ABCDEF", f = "", r = 0; r < d.length; r++)_ = d.charCodeAt(r), f += m.charAt(_ >>> 4 & 15) + m.charAt(15 & _); return f } function X(d) { for (var _ = Array(d.length >> 2), m = 0; m < _.length; m++)_[m] = 0; for (m = 0; m < 8 * d.length; m += 8)_[m >> 5] |= (255 & d.charCodeAt(m / 8)) << m % 32; return _ } function V(d) { for (var _ = "", m = 0; m < 32 * d.length; m += 8)_ += String.fromCharCode(d[m >> 5] >>> m % 32 & 255); return _ } function Y(d, _) { d[_ >> 5] |= 128 << _ % 32, d[14 + (_ + 64 >>> 9 << 4)] = _; for (var m = 1732584193, f = -271733879, r = -1732584194, i = 271733878, n = 0; n < d.length; n += 16) { var h = m, t = f, g = r, e = i; f = md5_ii(f = md5_ii(f = md5_ii(f = md5_ii(f = md5_hh(f = md5_hh(f = md5_hh(f = md5_hh(f = md5_gg(f = md5_gg(f = md5_gg(f = md5_gg(f = md5_ff(f = md5_ff(f = md5_ff(f = md5_ff(f, r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 0], 7, -680876936), f, r, d[n + 1], 12, -389564586), m, f, d[n + 2], 17, 606105819), i, m, d[n + 3], 22, -1044525330), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 4], 7, -176418897), f, r, d[n + 5], 12, 1200080426), m, f, d[n + 6], 17, -1473231341), i, m, d[n + 7], 22, -45705983), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 8], 7, 1770035416), f, r, d[n + 9], 12, -1958414417), m, f, d[n + 10], 17, -42063), i, m, d[n + 11], 22, -1990404162), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 12], 7, 1804603682), f, r, d[n + 13], 12, -40341101), m, f, d[n + 14], 17, -1502002290), i, m, d[n + 15], 22, 1236535329), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 1], 5, -165796510), f, r, d[n + 6], 9, -1069501632), m, f, d[n + 11], 14, 643717713), i, m, d[n + 0], 20, -373897302), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 5], 5, -701558691), f, r, d[n + 10], 9, 38016083), m, f, d[n + 15], 14, -660478335), i, m, d[n + 4], 20, -405537848), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 9], 5, 568446438), f, r, d[n + 14], 9, -1019803690), m, f, d[n + 3], 14, -187363961), i, m, d[n + 8], 20, 1163531501), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 13], 5, -1444681467), f, r, d[n + 2], 9, -51403784), m, f, d[n + 7], 14, 1735328473), i, m, d[n + 12], 20, -1926607734), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 5], 4, -378558), f, r, d[n + 8], 11, -2022574463), m, f, d[n + 11], 16, 1839030562), i, m, d[n + 14], 23, -35309556), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 1], 4, -1530992060), f, r, d[n + 4], 11, 1272893353), m, f, d[n + 7], 16, -155497632), i, m, d[n + 10], 23, -1094730640), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 13], 4, 681279174), f, r, d[n + 0], 11, -358537222), m, f, d[n + 3], 16, -722521979), i, m, d[n + 6], 23, 76029189), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 9], 4, -640364487), f, r, d[n + 12], 11, -421815835), m, f, d[n + 15], 16, 530742520), i, m, d[n + 2], 23, -995338651), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 0], 6, -198630844), f, r, d[n + 7], 10, 1126891415), m, f, d[n + 14], 15, -1416354905), i, m, d[n + 5], 21, -57434055), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 12], 6, 1700485571), f, r, d[n + 3], 10, -1894986606), m, f, d[n + 10], 15, -1051523), i, m, d[n + 1], 21, -2054922799), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 8], 6, 1873313359), f, r, d[n + 15], 10, -30611744), m, f, d[n + 6], 15, -1560198380), i, m, d[n + 13], 21, 1309151649), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 4], 6, -145523070), f, r, d[n + 11], 10, -1120210379), m, f, d[n + 2], 15, 718787259), i, m, d[n + 9], 21, -343485551), m = safe_add(m, h), f = safe_add(f, t), r = safe_add(r, g), i = safe_add(i, e) } return Array(m, f, r, i) } function md5_cmn(d, _, m, f, r, i) { return safe_add(bit_rol(safe_add(safe_add(_, d), safe_add(f, i)), r), m) } function md5_ff(d, _, m, f, r, i, n) { return md5_cmn(_ & m | ~_ & f, d, _, r, i, n) } function md5_gg(d, _, m, f, r, i, n) { return md5_cmn(_ & f | m & ~f, d, _, r, i, n) } function md5_hh(d, _, m, f, r, i, n) { return md5_cmn(_ ^ m ^ f, d, _, r, i, n) } function md5_ii(d, _, m, f, r, i, n) { return md5_cmn(m ^ (_ | ~f), d, _, r, i, n) } function safe_add(d, _) { var m = (65535 & d) + (65535 & _); return (d >> 16) + (_ >> 16) + (m >> 16) << 16 | 65535 & m } function bit_rol(d, _) { return d << _ | d >>> 32 - _ }

/**
 * Extracts a substring of given length from a string.
 * @param str A string
 * @param len Length of the substring to extract
 */
export function randomSubstring(str: string, len: number) {
  const rand = Math.floor((Math.random() * (str.length - len)))
  return str.substring(rand, len)
}

export const getTimezone = () => -Math.round((new Date().getTimezoneOffset() / 60))

/**
 * Return values that are not null or undefined.
 */
export function isNotNil<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

export function staticRandomSubset(mod: number, size: number, subsetSize: number) {
  if (subsetSize > size) throw new Error('subsetSize has to be smaller that size')
  if (size === 0) return new Set()
  let idx = mod % size
  const idxsSet = new Set([idx])
  for (let i = 1; i < subsetSize; i += 1) {
    idx = (idx + mod) % size
    while (idxsSet.has(idx)) {
      idx = (idx + 1) % size
    }
    idxsSet.add(idx)
  }
  return idxsSet
}

export function stringToCharCodesSum(str: string) {
  let sum = 0
  for (let i = 0; i < str.length; i += 1) {
    sum += str.charCodeAt(i)
  }
  return sum
}

export function secondsToHms(d: number) {
  // tslint:disable-next-line: no-parameter-reassignment
  d = Number(d)

  const h = Math.floor(d / 3600)
  const m = Math.floor(d % 3600 / 60)
  const s = Math.floor(d % 3600 % 60)

  return {
    h,
    m,
    s,
  }
}

export function millisToHms(d: number) {
  // tslint:disable-next-line: no-parameter-reassignment
  d = Number(d)

  const h = Math.floor(d / 1000 / 3600)
  const m = Math.floor(d / 1000 % 3600 / 60)
  const s = Math.floor(d / 1000 % 3600 % 60)

  return {
    h,
    m,
    s,
  }
}

/**
 * Creates an array of values ranging from `start` to `end`.
 * @param start Letter or number to start the array from.
 * @param end Letter or number to end the array at.
 */
export function range(start: number | string, end: number | string) {
  const foo: (number | string)[] = []

  if (typeof start !== 'number') {
    for (let i = start.toString().charCodeAt(0); i <= end.toString().charCodeAt(0); i += 1) {
      foo.push(String.fromCharCode(i))
    }
  }

  else {
    for (let i = Number(start); i <= Number(end); i += 1) {
      foo.push(i)
    }
  }

  return foo
}

export function isScrolledToBottom(el) {
  const scrolledToBottom = Math.ceil(el.scrollTop + el.offsetHeight) >= el.scrollHeight
  if (scrolledToBottom) return true
  return false
}

export function isScrolledToTop(el) {
  // const scrolledToTop = el.scrollHeight >= Math.ceil(el.scrollTop + el.offsetHeight)
  // if (scrolledToTop) return true
  if (el.scrollTop === 0) return true
  return false
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function isSessionDismissed(key: string) {
  const ss = sessionStorage.getItem('session-dismissed') || '[]'
  const parsedSs: string[] = JSON.parse(ss)
  const isDismissed = JSON.parse(ss).includes(key) || false

  return isDismissed
}

export function setSessionDismissed(key: string) {
  const ss = sessionStorage.getItem('session-dismissed') || '[]'
  const parsedSs: string[] = JSON.parse(ss)
  const stringified = JSON.stringify(parsedSs.concat([key]))
  sessionStorage.setItem('session-dismissed', stringified)
}

export function removeDuplicates(myArr, prop) {
  return myArr.filter((obj, pos, arr) => {
    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos
  })
}

export function escapeRegExp(text: string, escapeSpace?: boolean, reverse?: boolean) {
  if (escapeSpace) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\\s]/g, '\\$&')
  }
  if (reverse) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#]/g, '$&\\').split('').reverse().join('')
  }
  return text.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
}

export function isEmail(email: string) {
  const regex = new RegExp(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gim)
  return regex.test(email)
}

export const pick = (...props) => o => props.reduce((a, e) => ({ ...a, [e]: o[e] }), {})

export function isChild(element, classname) {
  if (typeof element.className !== 'object' && // SVGs are weird, man.
    element.className.split(' ').indexOf(classname) >= 0) {
    return true
  }

  if (element.tagName !== 'HTML') { // If you've reached the body, you've gone too far
    return element.parentNode && isChild(element.parentNode, classname)
  }

  return false
}

const originalFetch = require('isomorphic-fetch')
const fetch = require('fetch-retry')(originalFetch)

export const refetch = async (url, options?) => {
  const opts = {
    retryDelay: (attempt, error, response) => Math.pow(2, attempt) * 1000,
    retryOn: [408, 500, 502, 503, 504],
    ...options,
  }
  return fetch(url, opts)
}

export const filterDupes = (existing, incoming, field) => {
  const seen = new Set()
  const deduped = existing.concat(incoming).filter((el) => {
    const duplicate = seen.has(el[field])
    seen.add(el[field])
    return !duplicate
  })
  return deduped
}

export const percentageFromBottom = (el?) => {
  const h = el || document.documentElement
  const b = document.body
  const st = 'scrollTop'
  const sh = 'scrollHeight'

  const percent = (h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight) * 100

  return percent
}

export const allLettersMatch = (word, array) => {
  return word.split('').filter(l => l !== ' ').filter((each) => {
    return array.indexOf(each) === -1
  }).length === 0
}

export const isWordPuzzleCorrect = (placeholderAnswer, answerIn) => {
  if (!placeholderAnswer || !answerIn) return false
  const answer = answerIn.toUpperCase()
  return placeholderAnswer.toUpperCase().split('').every(c => c === ' ' || answer.includes(c))
}

export const isOnline = async () => {
  if (!window.navigator.onLine) return false

  const url = new URL(window.location.origin)
  url.searchParams.set('rand', Math.random().toString(36).substring(2, 15))

  try {
    const response = await fetch(url.toString(),
      { method: 'HEAD' },
    )

    return response.ok
  } catch {
    return false
  }
}

export const doArrow = (position, verticalAlign, horizontalAlign, whiteColor?) => {
  const opositeSide = {
    top: 'bottom',
    bottom: 'top',
    right: 'left',
    left: 'right',
  }

  if (!position || position === 'custom') {
    return {}
  }

  const width = 16
  const height = 12
  const color = whiteColor || '#F7DD79'
  const isVertical = position === 'top' || position === 'bottom'
  const spaceFromSide = 10
  const obj = {
    [isVertical ? 'borderLeft' : 'borderTop']: `${width / 2
      }px solid transparent`, // CSS Triangle width
    [isVertical ? 'borderRight' : 'borderBottom']: `${width / 2
      }px solid transparent`, // CSS Triangle width
    [`border${position[0].toUpperCase()}${position.substring(
      1,
    )}`]: `${height}px solid ${color}`, // CSS Triangle height
    [isVertical ? opositeSide[horizontalAlign] : verticalAlign]:
      height + spaceFromSide, // space from side
    [opositeSide[position]]: -height + 2,
  }

  return {
    '&::after': {
      content: "''",
      width: 0,
      height: 0,
      position: 'absolute',
      ...obj,
    },
  }
}

/**
 *
 * @param str path example /general/something
 * @param defaultVal some arbitrary value example "true"
 * @returns an object from the string
 */

export const expand = (str, defaultVal = {}) => {
  return str.split('/').filter(Boolean).reduceRight((acc, currentVal) => {
    return {
      [currentVal]: acc,
    }
  }, defaultVal)
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// // additional filter to remove a same course from different companies
export const getFilteredCourses = (sortedData, companies) => {
  const uniqueCourseIds = new Set()
  const filteredCourses = sortedData.filter((sub) => {
    const company = companies.find(c => c.id === sub.companyId)
    const distributionParams = company && company.subsDistributionParams.find(c => c.courseId === sub.courseId) || subsDistributionParamsFactory()
    const progress = sub.progress

    if (sortedData.some((d) => d.courseId === sub.courseId)) {
      if (uniqueCourseIds.has(sub.courseId)) return false
      if (isCourseExpired({ absoluteDeadline: distributionParams.absoluteDeadline, startedAt: progress?.startedAt || 0, relativeDeadline: distributionParams.relativeDeadline })) return false
    }

    uniqueCourseIds.add(sub.courseId)

    return true
  })

  return filteredCourses
}

// // additional filter to remove a same course from different companies
export const getFilteredCoursesOfActiveWorkspace = (sortedData) => {
  const uniqueCourseIds = new Set()
  const filteredCourses = sortedData.filter((data) => {
    const distributionParams = data.company && data.course && data.company.subsDistributionParams.find(c => c.courseId === data.course.id) || subsDistributionParamsFactory()
    const progress = data.subscription.progress

    if (sortedData.some((d) => d.course.id === data.course.id)) {
      if (uniqueCourseIds.has(data.course.courseId)) return false
      if (isCourseExpired({ absoluteDeadline: distributionParams.absoluteDeadline, startedAt: progress?.startedAt || 0, relativeDeadline: distributionParams.relativeDeadline })) return false
    }

    uniqueCourseIds.add(data.course.courseId)

    return true
  })

  return filteredCourses
}

export const stringSlicer = (str, length) => {
  if (str?.length > length) {
    return `${str.slice(0, length)}...`
  }
  return str
}

export const filterCompaniesWithDueDate = (companies: Company[], courseId: string) => {
  return companies
  .map(company => {
    const subsWithDueDates = company.subsDistributionParams.filter(s => s.dueDate !== null && s.courseId === courseId)
    if (subsWithDueDates.length > 0) {
      return {
        ...company,
        subsDistributionParams: subsWithDueDates
      };
    }
    return null
  })
  .filter(company => company !== null)
}

export const isRtlLanguage = (language) => {
  if (!language) return false
  const rtlLanguages = ['arabic', 'hebrew', 'persian', 'farsi', 'urdu', 'pashto', 'sindhi', 'yiddish', 'syriac', 'thaana', 'nko', 'mandaic', 'aramaic', 'rohingya', 'samaritan']
  return rtlLanguages?.includes(language?.toLowerCase())
};
